/**
 * KioskBookingSheet — front-desk one-stop booking peek for the /kiosk route.
 * Opens from the recent-activity strip; fetches the code-gated public booking
 * (same data the customer ticket shows) so an operator can handle any
 * logistics — status, money breakdown, waivers, add-ons, ticket link —
 * without leaving the kiosk. Currency-aware (USD/AED/…).
 *
 * Tap to Pay: when a balance is due and the kiosk runs inside the native
 * BookingFlows app (iPhone/Android) with an operator signed in, the sheet
 * offers in-person card collection via Stripe Terminal.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { X, Ticket, Users, Clock, CalendarDays, PenLine, CheckCircle2, Package, CreditCard, Copy, Check, Receipt, Smartphone, Loader2 } from 'lucide-react';
import api from '../utils/api';
import { API_URL } from '../config/api';
import { orgApi } from '../utils/apiV2';
import { collectInPerson, isTapToPaySupported } from '../utils/tapToPay';

// Human labels for raw payment_status values
const PAY_LABELS = {
  paid: 'Paid', completed: 'Paid', deposit_paid: 'Deposit paid',
  partial: 'Partially paid', partially_paid: 'Partially paid',
  refunded: 'Refunded', partial_refund: 'Partially refunded',
  pending: 'Unpaid', unpaid: 'Unpaid',
};

const SYMBOLS = { USD: '$', EUR: '€', GBP: '£' };

const chip = (label, tone) => {
  const tones = {
    green: 'bg-emerald-50 text-emerald-700',
    amber: 'bg-amber-50 text-amber-700',
    gray: 'bg-gray-100 text-gray-600',
    red: 'bg-red-50 text-red-700',
  };
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${tones[tone] || tones.gray}`}>{label}</span>;
};

export const KioskBookingSheet = ({ code, brand, onClose }) => {
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [tapBusy, setTapBusy] = useState(false);
  const [tapMsg, setTapMsg] = useState('');
  const [tapDone, setTapDone] = useState(false);
  const [tapError, setTapError] = useState('');

  const load = useCallback(() => {
    api.get(`/v2/public/bookings/${encodeURIComponent(code)}`)
      .then((r) => setBooking(r.data?.booking || r.data || null))
      .catch(() => setError(true));
  }, [code]);

  useEffect(() => {
    setBooking(null);
    setError(false);
    setTapDone(false);
    setTapError('');
    load();
  }, [code, load]);

  const paid = ['paid', 'completed'].includes(String(booking?.paymentStatus || ''));
  const addOns = booking?.selectedAddOns || [];
  const payRaw = String(booking?.paymentStatus || 'pending');
  const payLabel = PAY_LABELS[payRaw] || payRaw;
  const payTone = paid ? 'green' : payRaw.includes('refund') ? 'red' : 'amber';
  const cur = (booking?.currency || 'USD').toUpperCase();
  const isAED = cur === 'AED';
  // 🇦🇪 AED orgs: Arabic beside the English label
  const ar = (t) => (isAED ? <span dir="rtl" lang="ar" className="opacity-75 font-semibold"> {t}</span> : null);
  const money = (v) => {
    const n = Number(v || 0);
    const sym = SYMBOLS[cur];
    return sym ? `${sym}${n.toFixed(2)}` : `${cur} ${n.toFixed(2)}`;
  };
  // Secure ticket access — the backend short link mints a fresh 24h magic-link
  // token on every open and redirects to the ticket, so the guest/staff never
  // hits the email-verification wall.
  const ticketUrl = `${API_URL}/v2/public/t/${encodeURIComponent(code)}`;
  const copyTicketLink = () => {
    try { navigator.clipboard.writeText(ticketUrl); } catch (e) { /* noop */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  // Money breakdown — same immutable snapshot the customer ticket shows
  const base = Number(booking?.basePrice || 0);
  const addOnsTotal = Number(booking?.addOnsTotal || 0);
  const discount = Number(booking?.discountAmount || 0);
  const tax = Number(booking?.salesTax || 0);
  const fees = Number(booking?.platformFee || 0);
  const giftCard = Number(booking?.giftCardAmount || 0);
  // taxRate may be stored as decimal (0.0825) or percent (8.25) — normalize for display
  const taxRateRaw = Number(booking?.taxRate || 0);
  const taxPct = taxRateRaw > 1 ? taxRateRaw : taxRateRaw * 100;
  const taxLabel = taxPct > 0 ? ` (${parseFloat(taxPct.toFixed(2))}%)` : '';
  const total = Number(booking?.totalAmount || 0);
  const paidAmt = Number(booking?.paidAmount || 0);
  const balance = Number(booking?.remainingBalance || 0);
  const sigCount = (booking?.waiverSignatures || []).length || (booking?.waiverSigned ? 1 : 0);

  // Tap to Pay gate: native app + operator signed into this device + balance owed
  const operatorSignedIn = !!(localStorage.getItem('jwtToken') && localStorage.getItem('organizationId'));
  const canTap = balance > 0 && !isAED && !!booking?.id && isTapToPaySupported() && operatorSignedIn && !tapDone;

  const handleTapToPay = async () => {
    if (tapBusy) return;
    setTapBusy(true);
    setTapError('');
    setTapMsg('Preparing reader…');
    try {
      await collectInPerson({
        orgApi,
        booking: { id: booking.id },
        formatAmount: (a) => money(a),
        onStatus: (m) => setTapMsg(m),
      });
      setTapDone(true);
      load();
    } catch (e) {
      if (e?.code === 'canceled') {
        setTapError('Tap canceled — nothing was charged.');
      } else {
        const d = e?.response?.data?.detail;
        setTapError(typeof d === 'string' ? d : (e?.message || 'Tap to Pay could not complete.'));
      }
    } finally {
      setTapBusy(false);
      setTapMsg('');
    }
  };

  const row = (label, value, cls = 'text-gray-700') => (
    <div className={`flex justify-between text-xs py-0.5 ${cls}`}>
      <span>{label}</span>
      <span className="font-semibold">{value}</span>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center sm:justify-center"
      data-testid="kiosk-booking-sheet-overlay"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[85vh] overflow-y-auto shadow-xl"
        data-testid="kiosk-booking-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white px-5 pt-4 pb-3 flex items-start gap-3 border-b border-gray-100">
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-gray-900 truncate" data-testid="kiosk-sheet-service">
              {booking?.serviceName || (error ? 'Booking not found' : 'Loading…')}
            </p>
            <p className="text-xs text-gray-400 font-mono">{code}</p>
          </div>
          {booking && chip(payLabel, payTone)}
          <button
            onClick={onClose}
            data-testid="kiosk-sheet-close"
            aria-label="Close"
            className="h-8 w-8 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0 hover:text-gray-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {booking && (
          <div className="px-5 py-4 space-y-3 text-sm">
            <div className="flex items-center gap-2 text-gray-700" data-testid="kiosk-sheet-guest">
              <Users className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <span className="font-semibold">{booking.customerName}</span>
              <span className="text-gray-400">· {booking.partySize || 1} guest{(booking.partySize || 1) !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <CalendarDays className="w-4 h-4 text-gray-400 flex-shrink-0" />
              {booking.date || booking.preferredDate}
              <Clock className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
              {booking.startTime || booking.preferredTime}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="flex items-center gap-1.5 text-xs" data-testid="kiosk-sheet-waiver-chip">
                <PenLine className="w-3.5 h-3.5 text-gray-400" />
                {sigCount > 0
                  ? chip(`Waiver signed${sigCount > 1 ? ` ×${sigCount}` : ''}`, 'green')
                  : chip('Waiver not signed', 'amber')}
              </span>
              <span className="flex items-center gap-1.5 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5 text-gray-400" />
                {booking.checkInStatus === 'checked_in' ? chip('Checked in', 'green') : chip('Not checked in', 'gray')}
              </span>
            </div>

            {addOns.length > 0 && (
              <div className="bg-gray-50 rounded-xl p-3" data-testid="kiosk-sheet-addons">
                <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1.5">
                  <Package className="w-3.5 h-3.5" /> Add-ons
                </p>
                {addOns.map((a, i) => (
                  <div key={i} className="flex justify-between text-xs text-gray-700 py-0.5">
                    <span>{a.title || a.name}{a.quantity > 1 ? ` × ${a.quantity}` : ''}</span>
                    <span className="font-semibold">{money(a.price)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* 💵 Money breakdown — the full picture, taxes and fees included */}
            <div className="bg-gray-50 rounded-xl p-3" data-testid="kiosk-sheet-money">
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2 flex items-center gap-1.5">
                <Receipt className="w-3.5 h-3.5" /> Payment details{ar('تفاصيل الدفع')}
              </p>
              {row('Service', money(base))}
              {addOnsTotal > 0 && row('Add-ons', `+${money(addOnsTotal)}`)}
              {discount > 0 && row('Discount', `−${money(discount)}`, 'text-emerald-700')}
              {booking.taxInclusive
                ? row(`Tax${taxLabel}`, 'Included')
                : tax > 0 && row(`Tax${taxLabel}`, `+${money(tax)}`)}
              {fees > 0 && row('Service & processing fees', `+${money(fees)}`)}
              {giftCard > 0 && row('Gift card', `−${money(giftCard)}`, 'text-emerald-700')}
              <div className="flex justify-between border-t border-gray-200 mt-1.5 pt-1.5 text-sm">
                <span className="text-gray-500 font-medium">Total{ar('الإجمالي')}</span>
                <span className="font-extrabold text-gray-900" data-testid="kiosk-sheet-total">{money(total)}</span>
              </div>
              <div className="flex justify-between text-xs pt-1" data-testid="kiosk-sheet-paid">
                <span className="text-gray-500 flex items-center gap-1"><CreditCard className="w-3 h-3" /> Paid{ar('المدفوع')}</span>
                <span className={`font-bold ${paidAmt > 0 ? 'text-emerald-700' : 'text-gray-400'}`}>{money(paidAmt)}</span>
              </div>
              {balance > 0 && (
                <div className="flex justify-between text-xs pt-0.5" data-testid="kiosk-sheet-balance">
                  <span className="text-amber-700 font-medium">Balance due{ar('المبلغ المتبقي')}</span>
                  <span className="font-bold text-amber-700">{money(balance)}</span>
                </div>
              )}
            </div>

            {/* 📲 Tap to Pay — collect the balance in person (native app only) */}
            {canTap && (
              <button
                type="button"
                onClick={handleTapToPay}
                disabled={tapBusy}
                data-testid="kiosk-sheet-tap-to-pay"
                className="flex items-center justify-center gap-2 w-full rounded-full py-3 text-sm font-bold text-white bg-amber-500 hover:bg-amber-600 transition-colors active:scale-[0.98] disabled:opacity-70"
              >
                {tapBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
                {tapBusy ? (tapMsg || 'Working…') : `Tap to Pay · ${money(balance)}`}
              </button>
            )}
            {tapDone && (
              <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2.5" data-testid="kiosk-sheet-tap-success">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> Payment collected — tapped in person.
              </div>
            )}
            {tapError && (
              <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2.5" data-testid="kiosk-sheet-tap-error">{tapError}</p>
            )}

            <a
              href={ticketUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="kiosk-sheet-ticket-link"
              className="flex items-center justify-center gap-2 w-full rounded-full py-3 text-sm font-bold text-white transition-transform active:scale-[0.98]"
              style={{ backgroundColor: brand }}
            >
              <Ticket className="w-4 h-4" /> Open customer ticket{ar('فتح تذكرة العميل')}
            </a>

            {/* Shareable secure link — each open mints a fresh magic-link token */}
            <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2" data-testid="kiosk-sheet-ticket-url">
              <span className="flex-1 text-[11px] font-mono text-gray-500 truncate">{ticketUrl}</span>
              <button
                onClick={copyTicketLink}
                data-testid="kiosk-sheet-copy-ticket-link"
                aria-label="Copy ticket link"
                className="h-7 w-7 rounded-full bg-white shadow-sm text-gray-500 flex items-center justify-center flex-shrink-0 hover:text-gray-800 active:scale-95 transition-transform"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        )}
        {error && (
          <p className="px-5 py-6 text-sm text-gray-500" data-testid="kiosk-sheet-error">
            Couldn't load this booking — it may have expired or been removed.
          </p>
        )}
      </div>
    </div>
  );
};

export default KioskBookingSheet;
